import { Center, CloseButton, Drawer, Group, Loader, Text, Button } from "@mantine/core"
import { useTranslation } from "react-i18next"
import { modals } from "@mantine/modals"
import { useCallback, useRef } from "react"
import { Trans } from "react-i18next"
import { useAgency, useDeleteAgency } from "../hooks/useAgencies"
import AgencyDetailContent from "./AgencyDetailContent"
import AgencyFormContent from "./AgencyFormContent"
import { useUser } from "../../user/hooks/useUser"

export type DrawerMode = "view" | "create" | "edit"

interface Props {
  opened: boolean
  onClose: () => void
  mode: DrawerMode
  selectedId: number | null
  onDeleted: () => void
  onSwitchToEdit: () => void
}

export default function AgencyDrawer({ opened, onClose, mode, selectedId, onDeleted, onSwitchToEdit }: Props) {
  const { t } = useTranslation()
  const { data: agency, isLoading } = useAgency(mode !== "create" ? selectedId : null)
  const { data: userData } = useUser()
  const canManage = userData?.can?.manageAgencies ?? false
  const deleteMutation = useDeleteAgency()
  const formRef = useRef<{ submit: () => void }>(null)
  const isFormMode = mode === "edit" || mode === "create"

  const handleDelete = useCallback(() => {
    if (!agency?.id) return
    modals.openConfirmModal({
      title: t("agencies.confirmDelete"),
      children: <Text size="sm">{t("agencies.confirmDeleteMessage")}</Text>,
      labels: { confirm: t("common.delete"), cancel: t("common.cancel") },
      confirmProps: { color: "red" },
      onConfirm: () => {
        deleteMutation.mutate(agency.id, { onSuccess: () => onDeleted() })
      },
    })
  }, [agency, deleteMutation, onDeleted, t])

  const title = mode === "create"
    ? t("agencies.createTitle")
    : mode === "edit"
    ? t("agencies.editTitle")
    : agency?.name ?? ""

  return (
    <Drawer opened={opened} onClose={onClose} position="right" size="xl" withCloseButton={false}>
      <Drawer.Header>
        <Drawer.Title>
          <Text fw={600} size="md" lineClamp={1}>{title}</Text>
        </Drawer.Title>
        <Group gap="xs">
          {mode === "view" && canManage && (
            <>
              <Button size="xs" variant="default" onClick={onSwitchToEdit} leftSection={<i className="fa fa-pencil" />}>
                <Trans i18nKey="common.edit" />
              </Button>
              <Button size="xs" color="red" onClick={handleDelete} leftSection={<i className="fa fa-trash" />}>
                <Trans i18nKey="common.delete" />
              </Button>
            </>
          )}
          {isFormMode && (
            <Button size="xs" onClick={() => formRef.current?.submit()}>
              <Trans i18nKey={mode === "edit" ? "common.update" : "common.create"} />
            </Button>
          )}
          <CloseButton onClick={onClose} />
        </Group>
      </Drawer.Header>

      {mode === "create" ? (
        <AgencyFormContent ref={formRef} onSuccess={onClose} />
      ) : isLoading ? (
        <Center h="100%"><Loader /></Center>
      ) : !agency ? (
        <Text p="md"><Trans i18nKey="common.notFound" /></Text>
      ) : mode === "edit" ? (
        <AgencyFormContent ref={formRef} agency={agency} onSuccess={onClose} />
      ) : (
        <AgencyDetailContent agency={agency} />
      )}
    </Drawer>
  )
}
